import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext.jsx';

// Layouts
import AppLayout from './components/Layout/AppLayout.jsx';
import AuthLayout from './components/Layout/AuthLayout.jsx';

// Auth pages
import LoginPage from './pages/auth/LoginPage.jsx';

// Dashboard
import DashboardPage from './pages/DashboardPage.jsx';

// Gate Pass pages
import GatePassListPage from './pages/gatepasses/GatePassListPage.jsx';
import GatePassDetailPage from './pages/gatepasses/GatePassDetailPage.jsx';
import GatePassFormPage from './pages/gatepasses/GatePassFormPage.jsx';

// Delivery Notes
import DeliveryNoteListPage from './pages/deliverynotes/DeliveryNoteListPage.jsx';
import DeliveryNoteDetailPage from './pages/deliverynotes/DeliveryNoteDetailPage.jsx';
import DeliveryNoteFormPage from './pages/deliverynotes/DeliveryNoteFormPage.jsx';

// Masters
import MastersPage from './pages/masters/MastersPage.jsx';
import LocationsPage from './pages/masters/LocationsPage.jsx';
import UsersPage from './pages/masters/UsersPage.jsx';
import MerchantExportersPage from './pages/masters/MerchantExportersPage.jsx';
import PPCsPage from './pages/masters/PPCsPage.jsx';
import ProductsPage from './pages/masters/ProductsPage.jsx';
import ApprovalThresholdsPage from './pages/masters/ApprovalThresholdsPage.jsx';
import NotificationSettingsPage from './pages/masters/NotificationSettingsPage.jsx';
import CustomersPage from './pages/masters/CustomersPage.jsx';
import PortsPage from './pages/masters/PortsPage.jsx';
import SuppliersPage from './pages/masters/SuppliersPage.jsx';
import PurchaseCentersPage from './pages/masters/PurchaseCentersPage.jsx';
import FCodesPage from './pages/masters/FCodesPage.jsx';

// Files & Reports
import FileManagerPage from './pages/FileManagerPage.jsx';
import ReportsPage from './pages/reports/ReportsPage.jsx';
import AuditLogPage from './pages/AuditLogPage.jsx';

// ITR
import ITRListPage from './pages/ITRListPage.jsx';
import ITRFormPage from './pages/ITRFormPage.jsx';

function PrivateRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <Routes>
      {/* Public */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
      </Route>

      {/* Protected */}
      <Route element={<PrivateRoute><AppLayout /></PrivateRoute>}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardPage />} />

        {/* Gate Passes */}
        <Route path="/gate-passes" element={<GatePassListPage />} />
        <Route path="/gate-passes/new" element={<GatePassFormPage />} />
        <Route path="/gate-passes/:id" element={<GatePassDetailPage />} />
        <Route path="/gate-passes/:id/edit" element={<GatePassFormPage />} />

        {/* Delivery Notes */}
        <Route path="/delivery-notes" element={<DeliveryNoteListPage />} />
        <Route path="/delivery-notes/new" element={<DeliveryNoteFormPage />} />
        <Route path="/delivery-notes/:id" element={<DeliveryNoteDetailPage />} />

        {/* ITR */}
        <Route path="/itrs" element={<ITRListPage />} />
        <Route path="/itrs/new" element={<ITRFormPage />} />

        {/* Files */}
        <Route path="/files" element={
          <PrivateRoute roles={['SUPER_ADMIN', 'ADMIN']}>
            <FileManagerPage />
          </PrivateRoute>
        } />

        {/* Reports */}
        <Route path="/reports" element={
          <PrivateRoute roles={['SUPER_ADMIN', 'ADMIN', 'MANAGER']}>
            <ReportsPage />
          </PrivateRoute>
        } />

        {/* Audit Log */}
        <Route path="/audit" element={
          <PrivateRoute roles={['SUPER_ADMIN', 'ADMIN']}>
            <AuditLogPage />
          </PrivateRoute>
        } />

        {/* Masters — Super Admin only */}
        <Route path="/masters" element={
          <PrivateRoute roles={['SUPER_ADMIN']}>
            <MastersPage />
          </PrivateRoute>
        }>
          <Route index element={<Navigate to="locations" replace />} />
          <Route path="locations" element={<LocationsPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="merchant-exporters" element={<MerchantExportersPage />} />
          <Route path="ppcs" element={<PPCsPage />} />
          <Route path="products" element={<ProductsPage />} />
          <Route path="customers" element={<CustomersPage />} />
          <Route path="ports" element={<PortsPage />} />
          <Route path="suppliers" element={<SuppliersPage />} />
          <Route path="purchase-centers" element={<PurchaseCentersPage />} />
          <Route path="fcodes" element={<FCodesPage />} />
          <Route path="approval-thresholds" element={<ApprovalThresholdsPage />} />
          <Route path="notification-settings" element={<NotificationSettingsPage />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
